<template>
  <div
    :class="['history-setting-menu', { collapsed: isCollapsed }]"
    v-loading="saving"
    element-loading-text="保存中…"
    element-loading-spinner="el-icon-loading"
    element-loading-background="rgba(255, 255, 255, 0.75)"
  >
    <div class="menu-header">
      <div class="menu-title">個人設定</div>
      <div class="menu-actions">
        <el-button
          type="primary"
          class="action action-save"
          :disabled="saving"
          @click="handleSave"
        >
        <!-- Use the provided asset icon for the save action. -->
        <!-- <img
          class="save-icon-image"
          src="@/assets/img/save.png"
          alt="save"
        /> --> 
        保存
        </el-button>
        <el-button 
          type="text"
          class="icon-button collapse-button"
          @click="collapseMenu">
					<i :class="isCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"></i>
				</el-button>
        <el-button
          type="text"
          class="icon-button close-button"
          @click="handleClose"
        >
          <i class="el-icon-close"></i>
        </el-button>
      </div>
    </div>

    <div class="menu-content" v-if="!isCollapsed" >
      <div class="menu-description">
        <div class="title">パネル表示のカスタマイズ</div>
        <el-button type="primary" class="action edit-button" @click="handleEdit">編集</el-button>
      </div>
      <div class="menu-help">
        編集を開始すると、表示中のパネル右上に「×（閉じる）」ボタンが表示され、不要なチャートパネルを見ながら、一括で非表示にすることができます。
      </div>
      <div class="chart-section">
        <div class="section-title">表示中のチャートパネル</div>
        <div v-if="localUsedChartList.length === 0" class="empty-text">
          表示中の chart はありません
        </div>
        <div
          v-for="chart in localUsedChartList"
          :key="`used-${chart.name}`"
          class="chart-row"
        >
          <div class="chart-name">{{ chart.displayName }}</div>
          <el-button
            type="text"
            class="chart-action-button"
            @click="moveToUnused(chart.name)"
          >
            非表示
          </el-button>
        </div>
      </div>

      <div class="chart-section">
        <div class="section-title">非表示中のチャートパネル</div>
        <div v-if="localUnusedChartList.length === 0" class="empty-text">
          非表示の chart はありません
        </div>
        <div
          v-for="chart in localUnusedChartList"
          :key="`unused-${chart.name}`"
          class="chart-row"
        >
          <div class="chart-name">{{ chart.displayName }}</div>
          <el-button
            type="text"
            class="chart-action-button"
            @click="moveToUsed(chart.name)"
          >
            再表示
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api";
var _ = require("lodash");

export default {
  name: "HistorySettingMenu",
  data() {
    return {
      // Keep a local editable copy so changes are not persisted until save is clicked.
      localUsedChartList: [],
      localUnusedChartList: [],
      // Track the saving state to prevent duplicate submissions.
      saving: false,
      isCollapsed: false,
    };
  },
  created() {
    this.initializeLocalLists();
  },
  methods: {
    // Create local copies from root state when the drawer is opened.
    initializeLocalLists() {
      this.localUsedChartList = _.cloneDeep(this.$root.usedChartList || []);
      this.localUnusedChartList = _.cloneDeep(this.$root.unusedChartList || []);
      this.sortLocalLists();
    },

    // Keep both local lists ordered by their original index for stable rendering.
    sortLocalLists() {
      this.localUsedChartList = _.sortBy(this.localUsedChartList, function (o) {
        return o.index;
      });
      this.localUnusedChartList = _.sortBy(this.localUnusedChartList, function (o) {
        return o.index;
      });
    },

    // Move one chart from the visible list to the hidden list without saving immediately.
    moveToUnused(chartName) {
      const targetIndex = this.localUsedChartList.findIndex(
        (chart) => chart.name === chartName
      );
      if (targetIndex === -1) {
        return;
      }

      const targetChart = this.localUsedChartList[targetIndex];
      this.localUsedChartList.splice(targetIndex, 1);
      this.localUnusedChartList.push(targetChart);
      this.sortLocalLists();
    },

    // Move one chart from the hidden list back to the visible list without saving immediately.
    moveToUsed(chartName) {
      const targetIndex = this.localUnusedChartList.findIndex(
        (chart) => chart.name === chartName
      );
      if (targetIndex === -1) {
        return;
      }

      const targetChart = this.localUnusedChartList[targetIndex];
      this.localUnusedChartList.splice(targetIndex, 1);
      this.localUsedChartList.push(targetChart);
      this.sortLocalLists();
    },

    // Build the payload list used to update chart visibility and display order in the database.
    buildSavePayload() {
      const payload = [];
      const userId = this.$root.user.user_id;

      this.localUsedChartList.forEach((chart, index) => {
        payload.push({
          user_id: userId,
          name: chart.name,
          checkedFlg: true,
          index: index + 1,
        });
      });

      this.localUnusedChartList.forEach((chart, index) => {
        payload.push({
          user_id: userId,
          name: chart.name,
          checkedFlg: false,
          index: this.localUsedChartList.length + index + 1,
        });
      });

      return payload;
    },

    // Persist all pending changes only when the save icon is clicked.
    async handleSave() {
      if (this.saving) {
        return;
      }

      this.saving = true;
      const payloadList = this.buildSavePayload();

      try {
        await Promise.all(
          payloadList.map((item) =>
            Promise.all([
              api.saveUserEchartStatus({
                user_id: item.user_id,
                name: item.name,
                checkedFlg: item.checkedFlg,
              }),
              api.saveUserEcharIndex({
                user_id: item.user_id,
                name: item.name,
                index: item.index,
              }),
            ])
          )
        );

        // Sync the root lists after save so the history page reflects the latest configuration.
        this.$root.usedChartList = _.cloneDeep(this.localUsedChartList).map(
          (chart, index) => {
            return {
              ...chart,
              index: index + 1,
            };
          }
        );
        this.$root.unusedChartList = _.cloneDeep(this.localUnusedChartList).map(
          (chart, index) => {
            return {
              ...chart,
              index: this.$root.usedChartList.length + index + 1,
            };
          }
        );

        this.handleClose();
      } catch (err) {
        console.log(err);
      } finally {
        this.saving = false;
      }
    },

    // Close the drawer without persisting local changes.
    handleClose() {
      this.$root.editMode = false;
      this.$root.showHistorySetting = false;
      this.isCollapsed = false
      this.$emit('collapse-change',false)
    },
    collapseMenu(){
      this.isCollapsed = !this.isCollapsed
      this.$emit('collapse-change',this.isCollapsed)
    },
    handleEdit(){
      this.$root.editMode = true
      this.collapseMenu()
    }
  },
};
</script>

<style lang="scss" scoped>
.history-setting-menu {
  height: 100%;
  background: #ffffff;
  padding: 0 0 16px 0;
  box-sizing: border-box;
	// overflow-y: hidden;
	&.collapsed {
		height: auto;
		padding-bottom: 0;
  }
}

.menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 16px 6px 16px;
  border-bottom: 1px solid #dcdfe6;
}

.menu-title {
  font-size: 16px;
  color: #333333;
  line-height: 1.2;
}

.menu-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.icon-button {
  padding: 4px;
  font-size: 18px;
}

.save-button {
  color: #606266;
}
.collapse-button {
  color: #606266;
}
.close-button {
  color: #606266;
}

.menu-content {
  padding: 12px 16px 2px 16px;
}
.menu-description {
    display: flex;
    -ms-flex-align: center;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
}
.edit-button {
}

.menu-help {
  font-size: 12px;
  color: #606266;
  line-height: 1.6;
  margin-bottom: 18px;
}

.chart-section {
  margin-bottom: 16px;
}

.section-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}

.chart-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 36px;
  padding: 0 0 0 4px;
  border-top: 1px solid #ebeef5;
}

.chart-row:last-child {
  border-bottom: 1px solid #ebeef5;
}

.chart-name {
  flex: 1;
  font-size: 13px;
  color: #303133;
  padding-right: 12px;
  line-height: 1.4;
}
.action {
  width: 50px;
  font-size: 12px;
  border-radius: 5px;
  background-color: #2f70c1;
  color: #fff;
  height: 24px;
}

.chart-action-button {
  color: #2f70c1;
  font-size: 12px;
  padding: 0;
  transition: color 0.2s ease, opacity 0.2s ease;
}

.close-button:hover, 
.close-button:focus {
    color: #606266;
}

.collapse-button:hover, 
.collapse-button:focus {
    color: #606266;
}
.chart-action-button:hover,
.chart-action-button:focus {
  color: #5b8fcec9;
  opacity: 1;
}

.empty-text {
  font-size: 12px;
  color: #909399;
  padding: 8px 4px;
  border-top: 1px solid #ebeef5;
  border-bottom: 1px solid #ebeef5;
}

.save-icon-image {
  width: 16px;
  height: 16px;
  display: block;
}
</style>
